import React, { useEffect, useState } from "react";
import ModalShell from "./ModalShell";
import ProgramColorInput from "./ProgramColorInput";
import { parseProgramColor } from "../shared/utils/programColor";

/**
 * Create or edit a Program (ModalShell).
 * Parent owns API via onSubmit({ name, description, category, color, _id? }).
 */
export default function ProgramEditorModal({
  open,
  mode = "create",
  program = null,
  categoryOptions = [],
  onCancel,
  onSubmit,
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [color, setColor] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isEdit = mode === "edit";

  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setError("");
    if (isEdit && program) {
      const colorParsed = parseProgramColor(program.color);
      setName(program.name || "");
      setDescription(program.description || "");
      setCategory(program.category || "");
      setColor(colorParsed.ok ? colorParsed.color : "");
    } else {
      setName("");
      setDescription("");
      setCategory("");
      setColor("");
    }
  }, [open, mode, program, isEdit]);

  const categoryList = [...new Set((categoryOptions || []).map((c) => String(c || "").trim()).filter(Boolean))].sort();

  async function handleSubmit(e) {
    e.preventDefault();
    const nameVal = (name != null ? String(name) : "").trim();
    if (!nameVal) {
      setError("Name is required.");
      return;
    }
    const colorParsed = parseProgramColor(color);
    if (!colorParsed.ok) {
      setError(colorParsed.error);
      return;
    }

    const payload = {
      name: nameVal,
      description: (description || "").trim(),
      category: (category || "").trim(),
      color: colorParsed.color,
    };
    if (isEdit && program?._id) payload._id = program._id;

    setSubmitting(true);
    setError("");
    try {
      await onSubmit(payload);
    } catch (err) {
      const msg = err && typeof err.message === "string" ? err.message.trim() : "";
      setError(msg || "Failed to save program.");
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
  }

  const title = isEdit ? "Edit Program" : "New Program";
  const submitLabel = isEdit ? "Save" : "Create";

  return (
    <ModalShell
      open={open}
      onClose={submitting ? undefined : onCancel}
      title={title}
      maxWidth={520}
      zIndex={1090}
      closeOnOverlayClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={submitting}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="program-editor-modal-form"
            className="btn btn-primary"
            disabled={submitting || !String(name || "").trim()}
          >
            {submitting ? "Saving…" : submitLabel}
          </button>
        </>
      }
    >
      <form id="program-editor-modal-form" className="modal-form" onSubmit={handleSubmit}>
        {error ? (
          <div style={{ color: "#c92a2a", fontSize: 13, marginBottom: 12 }}>{error}</div>
        ) : null}

        <div className="form-field">
          <label htmlFor="program-editor-name">Name *</label>
          <input
            id="program-editor-name"
            type="text"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={submitting}
            autoFocus={!isEdit}
            placeholder="Name"
          />
        </div>

        <div className="form-field">
          <label htmlFor="program-editor-description">Description</label>
          <input
            id="program-editor-description"
            type="text"
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={submitting}
            placeholder="Description"
          />
        </div>

        <div className="form-field">
          <label htmlFor="program-editor-category">Category</label>
          <input
            id="program-editor-category"
            type="text"
            className="input"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={submitting}
            placeholder="Category"
            list="program-editor-category-list"
          />
          <datalist id="program-editor-category-list">
            {categoryList.map((cat) => (
              <option key={cat} value={cat} />
            ))}
          </datalist>
        </div>

        <div className="form-field">
          <label htmlFor="program-editor-color">Color</label>
          <ProgramColorInput
            id="program-editor-color"
            value={color}
            onChange={setColor}
            disabled={submitting}
          />
        </div>
      </form>
    </ModalShell>
  );
}
